import {useState, useEffect, useCallback, useRef} from 'react';
import {toast} from 'react-toastify';
import {ErrorMessage} from '@hookform/error-message';
import {useForm, Controller} from 'react-hook-form';
import FormInput from '../../components/FoodMenu/FormInput';
import FormMultiSelectInput from '../../components/Settings/DropdownMultiSelectInput';
import {Table} from 'react-bootstrap';
import {
    updateReserSettings,
    getReservationDetails,
    updateSlotSettings,
} from '../../../services/SettingsService';
import {objectToFormData} from '../../../utils/objectToFormData';
import TimePicker from '../../components/Settings/TimePicker';

const notify = (id, type, message, time) => {
    toast.update(id, {
        render: message,
        type: type,
        position: 'top-center',
        autoClose: time,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        isLoading: false,
    });
};

const ReservationSettings = props => {
    const [resdetails, setResdetails] = useState();
    const [occasionList, setOccasionList] = useState();
    const [slotList, setSlotList] = useState([]);
    const [loading, setLoading] = useState(true); // Add loading state

    const uploadRef = useRef(null);
    const days = [
        {
            name: 'reservation_days.sun', // Fixed: was 'son' should be 'sun'
            label: 'Sunday',
        },
        {
            name: 'reservation_days.mon',
            label: 'Monday',
        },
        {
            name: 'reservation_days.tue',
            label: 'Tuesday',
        },
        {
            name: 'reservation_days.wed',
            label: 'Wednesday', // Fixed capitalization
        },
        {
            name: 'reservation_days.thu',
            label: 'Thursday',
        },
        {
            name: 'reservation_days.fri',
            label: 'Friday',
        },
        {
            name: 'reservation_days.sat',
            label: 'Saturday',
        },
    ];

    const {
        handleSubmit,
        formState: {errors},
        register,
        setValue,
        control,
    } = useForm({
        mode: 'all',
        defaultValues: {
            slot_end_time: `${String(new Date('2000-01-01 10:00 AM').getHours()).padStart(2, '0')}:${
              String(new Date('2000-01-01 10:00 AM').getMinutes()).padStart(2, '0')
            }:00`,
            slot_start_time: `${String(new Date('2000-01-01 11:00 PM').getHours()).padStart(2, '0')}:${
              String(new Date('2000-01-01 11:00 PM').getMinutes()).padStart(2, '0')
            }:00`,
        },
    });

    useEffect(() => {
        const fetchReservationDetails = async () => {
            try {
                setLoading(true);
                const res = await getReservationDetails();
                
                // Handle the response structure - data is in an array
                const data = res?.data?.[0] || res; // Handle both possible structures
                setResdetails(data);


            } catch (error) {
                console.error('Error fetching reservation details:', error);
                toast.error('Failed to load reservation details');
            } finally {
                setLoading(false);
            }
        };

        fetchReservationDetails();
    }, []);

    useEffect(() => {
        if (resdetails) {
            try {
                // Handle occasion list
                let listToPreload = [];
                let suggestionList = [];

                if (resdetails.occassion_list && Array.isArray(resdetails.occassion_list)) {
                    listToPreload = resdetails.occassion_list.map(each => ({
                        label: each,
                        value: each
                    }));
                }

                if (resdetails.occ_suggestion_list && Array.isArray(resdetails.occ_suggestion_list)) {
                    suggestionList = resdetails.occ_suggestion_list.map(each => ({
                        label: each,
                        value: each
                    }));
                }

                setOccasionList([...suggestionList, ...listToPreload]);
                setValue('occassion_list', listToPreload);

                // Handle slots list
                if (resdetails.slots_list && Array.isArray(resdetails.slots_list)) {
                    setSlotList(resdetails.slots_list);
                } else {
                    setSlotList([]); // Set empty array if no slots
                }

                // Set form values with proper mapping
                const fieldMapping = {
                    'reservation_days': resdetails.reservation_days,
                    'slot_start_time': resdetails.slot_start_time ? 
                        convertTimeFormat(resdetails.slot_start_time) : 
                        '10:00:00',
                    'slot_end_time': resdetails.slot_end_time ? 
                        convertTimeFormat(resdetails.slot_end_time) : 
                        '23:00:00',
                    'slot_duration': resdetails.slot_duration || '',
                    'reservation_prior_time': resdetails.reservation_prior_time,
                    'res_charge': resdetails.res_charge,
                    'valet_parking': resdetails.valet_parking,
                    'slots_list': resdetails.slots_list || [],
                };

                // Set individual day values if reservation_days exists
                if (resdetails.reservation_days) {
                    Object.keys(resdetails.reservation_days).forEach(day => {
                        setValue(`reservation_days.${day}`, resdetails.reservation_days[day]);
                    });
                }

                // Set other form values
                Object.entries(fieldMapping).forEach(([field, value]) => {
                    if (value !== undefined) {
                        setValue(field, value);
                    }
                });
            } catch (error) {
                console.error('Error processing reservation details:', error);
            }
        }
    }, [setValue, resdetails]);

    // Helper function to convert time format
    const convertTimeFormat = (timeStr) => {
        try {
            // Convert "08:00:00 AM" to "08:00:00" format
            if (timeStr.includes('AM') || timeStr.includes('PM')) {
                const date = new Date(`2000-01-01 ${timeStr}`);
                return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:00`;
            }
            return timeStr;
        } catch (error) {
            console.error('Error converting time format:', error);
            return timeStr;
        }
    };

    const onSubmit = async (data) => {
        try {
            // prepare data here ...
            const toaster = toast.loading('Updating...');
            
            // Create a copy of the data to avoid modifying the original
            const processedData = {...data};
            
            // Extract only the values from the occasion list to prevent duplication
            // The FormMultiSelectInput returns an array of objects with label and value properties
            // But we only need to send the values to the backend
            if (processedData.occassion_list && Array.isArray(processedData.occassion_list)) {
                processedData.occassion_list = processedData.occassion_list.map(item => item.value);
            }
            
            let postData = objectToFormData(processedData);
            let res = await updateReserSettings(postData);
            
            if (res && res.status === 'success') {
                notify(toaster, 'success', 'Updated Reservation Settings!', 2000);
            } else {
                notify(toaster, 'error', 'Update failed', 2000);
            }
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };
    


    const slotChange = async (id) => {
        try {
            // Update local state
            setSlotList(prevSlotList =>
                prevSlotList.map(slot => 
                    slot.id === id ? {...slot, status: !slot.status} : slot
                )
            );

            // Find the changed slot
            const changedSlot = slotList.find(slot => slot.id === id);
            if (changedSlot) {
                const newStatus = !changedSlot.status;
                // Pass status in a proper data object format
                const formData = new FormData();
                formData.append('status', newStatus);
                await updateSlotSettings(id, formData);
            }
        } catch (error) {
            console.error('Error updating slot status:', error);
            toast.error('Failed to update slot status');
            
            // Revert the local state change on error
            setSlotList(prevSlotList =>
                prevSlotList.map(slot => 
                    slot.id === id ? {...slot, status: !slot.status} : slot
                )
            );
        }
    };

    // Show loading state
    if (loading) {
        return (
            <div className='modal-body'>
                <div className='d-flex justify-content-center align-items-center' style={{height: '400px'}}>
                    <div id='preloader-inner'>
                        <div className='sk-three-bounce-inner'>
                            <div className='sk-child sk-bounce1'></div>
                            <div className='sk-child sk-bounce2'></div>
                            <div className='sk-child sk-bounce3'></div>
                        </div>
                    </div>
                    <p className='ml-3'>Loading reservation settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className='modal-body'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='d-flex row'>
                    <div className='col-md-6 col-xl-3 col-xxl-6 mb-3 picker-data text-black font-w500'>
                        <div className='color-time-picker'>
                            <TimePicker
                                label={'Start time'}
                                name={'slot_start_time'}
                                required={true}
                                Controller={Controller}
                                control={control}
                                errors={errors}
                                id={'slot_start_time'}
                                ErrorMessage={ErrorMessage}
                                register={{
                                    ...register('slot_start_time'),
                                    required: 'Reservation Start time is required!',
                                }}
                            />
                        </div>
                    </div>
                    
                    <div className='col-md-6 col-xl-3 col-xxl-6 mb-3 picker-data'>
                        <div className='color-time-picker'>
                            <TimePicker
                                label={'End time'}
                                name={'slot_end_time'}
                                Controller={Controller}
                                control={control}
                                errors={errors}
                                id={'slot_end_time'}
                                required={true}
                                ErrorMessage={ErrorMessage}
                                register={{
                                    ...register('res_endtime'),
                                    required: 'Reservation End time is required!',
                                }}
                            />
                        </div>
                    </div>

                    <div className='col-10'>
                        <FormInput
                            label={'Reservation Charge'}
                            id={'res_charge'}
                            name={'res_charge'}
                            required={true}
                            type={'number'}
                            ErrorMessage={ErrorMessage}
                            register={{
                                ...register('res_charge'),
                                required: 'Reservation charge is required!',
                            }}
                            errors={errors}
                        />
                    </div>

                    <div className='col-10'>
                        <FormInput
                            label={'Reservation prior time (minutes)'}
                            id={'reservation_prior_time'}
                            name={'reservation_prior_time'}
                            required={true}
                            type={'number'}
                            ErrorMessage={ErrorMessage}
                            register={{
                                ...register('reservation_prior_time', {
                                    required: 'Reservation prior time is required!',
                                }),
                            }}
                            errors={errors}
                        />
                    </div>

                    <div className='col-10'>
                        <FormInput
                            label={'Slot Length (minutes)'}
                            id={'slot_duration'}
                            name={'slot_duration'}
                            type={'number'}
                            required={true}
                            ErrorMessage={ErrorMessage}
                            register={{
                                ...register('slot_duration', {
                                    required: 'Slot length is required!',
                                }),
                            }}
                            errors={errors}
                        />
                    </div>

                    <label className='text-black font-w500 col-12'>
                        Reservation Slots
                    </label>
                    <div className='col-10 tableContainer'>
                        <div className='table-responsive order-list card-table' style={{height: '400px'}}>
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th><strong>Slots</strong></th>
                                        <th><strong>Status</strong></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {slotList?.length ? (
                                        slotList.map(slots => (
                                            <tr key={slots.id}>
                                                <td>
                                                    <div className='d-flex align-items-center'>
                                                        <span className='w-space-no'>
                                                            {slots.slotes}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className='custom-control custom-checkbox mb-3'>
                                                        <input
                                                            type='checkbox'
                                                            checked={slots.status}
                                                            className='custom-control-input'
                                                            onChange={() => slotChange(slots.id)}
                                                            id={slots.id}
                                                        />
                                                        <label
                                                            className='custom-control-label'
                                                            htmlFor={slots.id}
                                                        >
                                                            Active
                                                        </label>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="text-center">
                                                No slots available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    <label className='text-black font-w500 col-12 mt-3'>
                        Reservation days
                    </label>
                    {days.map((day, i) => (
                        <div key={i} className='col-4'>
                            <div className='custom-control custom-checkbox mb-3 checkbox-info'>
                                <input
                                    type='checkbox'
                                    className='custom-control-input'
                                    {...register(day.name)}
                                    id={day.name}
                                />
                                <label className='custom-control-label' htmlFor={day.name}>
                                    {day.label}
                                </label>
                            </div>
                        </div>
                    ))}

                    <div className='col-10'>
                        <FormMultiSelectInput
                            label={'Occasion List'}
                            id={'val-occasion'}
                            name={'occassion_list'}
                            ErrorMessage={ErrorMessage}
                            Controller={Controller}
                            control={control}
                            errors={errors}
                            required={true}
                            options={occasionList || [{value: null, label: 'Loading...'}]}
                        />
                    </div>
                </div>

                <div className='custom-control custom-checkbox mb-3'>
                    <input
                        type='checkbox'
                        className='custom-control-input'
                        {...register('valet_parking')}
                        id='val_parking'
                    />
                    <label className='custom-control-label' htmlFor='val_parking'>
                        Parking
                    </label>
                </div>

                <button className='btn btn-primary btn-block btn-md mb-2 mr-auto'>
                    Update
                </button>
            </form>
        </div>
    );
};

export default ReservationSettings;